import { FeConstants } from 'constants/FeConstants';
import { DynamicsEnvironment } from './useDynamicsEnvironments';

const DYNAMICS_FLOW_KEY = 'dynamics_oauth_flow';
const DYNAMICS_ERROR_KEY = 'dynamics_connect_error';
const DYNAMICS_START_PATH = '/connect/dynamics/start';
export const DYNAMICS_RETURN_PATH_PARAM = 'returnPath';

/**
 * Where the connect stands between the two Microsoft sign-ins. Kept in
 * sessionStorage, never in the URL or the OAuth `state`, so the callback reads
 * its step from here and a reload of the picker keeps the list.
 */
export type DynamicsOauthFlow =
  | { step: 'environments'; state: string; returnPath: string }
  | {
      step: 'picking';
      returnPath: string;
      environments: DynamicsEnvironment[];
    }
  | {
      step: 'connect';
      state: string;
      returnPath: string;
      environment: DynamicsEnvironment;
    };

const generateState = (): string => {
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

const saveFlow = (flow: DynamicsOauthFlow) =>
  sessionStorage.setItem(DYNAMICS_FLOW_KEY, JSON.stringify(flow));

export const getDynamicsOauthFlow = (): DynamicsOauthFlow | null => {
  const stored = sessionStorage.getItem(DYNAMICS_FLOW_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as DynamicsOauthFlow;
  } catch {
    return null;
  }
};

export const clearDynamicsOauthFlow = (): void =>
  sessionStorage.removeItem(DYNAMICS_FLOW_KEY);

/**
 * Sign-in 1: mints the `state` nonce, remembers where the user started, and
 * returns the Microsoft URL that lists their environments. sessionStorage is
 * per-origin, so this must run on the origin Microsoft redirects back to — see
 * `getDynamicsConnectUrl`.
 */
export const buildDynamicsDiscoveryLoginUrl = (returnPath: string): string => {
  const state = generateState();
  saveFlow({ step: 'environments', state, returnPath });
  return FeConstants.DYNAMICS_DISCOVERY_LOGIN_URL(state);
};

/**
 * Where a "Connect Dynamics 365" click sends the browser. A front end served
 * from another origin than the registered callback (local development) first
 * hops to that origin's start route, so the nonce is minted where the callback
 * reads it.
 */
export const getDynamicsConnectUrl = (returnPath: string): string => {
  clearDynamicsConnectError();
  const callbackOrigin = new URL(FeConstants.DYNAMICS_REDIRECT_URI).origin;
  if (callbackOrigin !== window.location.origin) {
    const params = new URLSearchParams({
      [DYNAMICS_RETURN_PATH_PARAM]: returnPath,
    });
    return `${callbackOrigin}${DYNAMICS_START_PATH}?${params.toString()}`;
  }
  return buildDynamicsDiscoveryLoginUrl(returnPath);
};

/** Holds the environments for the picker shown on the page the user started from. */
export const saveDynamicsEnvironmentsToPick = (
  returnPath: string,
  environments: DynamicsEnvironment[],
): void => saveFlow({ step: 'picking', returnPath, environments });

/**
 * Sign-in 2: mints a fresh nonce for the chosen environment and returns the
 * Microsoft URL that connects it. Usually no Microsoft screen shows: same
 * session, consent already given.
 */
export const buildDynamicsEnvironmentLoginUrl = (
  returnPath: string,
  environment: DynamicsEnvironment,
): string => {
  const state = generateState();
  saveFlow({ step: 'connect', state, returnPath, environment });
  return FeConstants.DYNAMICS_ENVIRONMENT_LOGIN_URL(
    state,
    environment.tenantId,
    environment.url ?? '',
  );
};

export const isDynamicsOauthStateValid = (
  flow: DynamicsOauthFlow | null,
  state: unknown,
): flow is Exclude<DynamicsOauthFlow, { step: 'picking' }> =>
  !!flow &&
  flow.step !== 'picking' &&
  typeof state === 'string' &&
  state !== '' &&
  state === flow.state;

/** The last connect failure, shown as the banner on the Dynamics card until the next attempt. */
export const storeDynamicsConnectError = (message: string): void =>
  sessionStorage.setItem(DYNAMICS_ERROR_KEY, message);

export const getDynamicsConnectError = (): string | null =>
  sessionStorage.getItem(DYNAMICS_ERROR_KEY);

export const clearDynamicsConnectError = (): void =>
  sessionStorage.removeItem(DYNAMICS_ERROR_KEY);
